import { useEffect, useState } from 'react';
import { CATS, CONFIG, colorHex } from '../data/catalog.js';
import { fmt } from '../lib/money.js';
import { useStore, computeUnit } from '../lib/store.jsx';
import { useI18n } from '../lib/i18n.jsx';
import ProductCard from '../components/ProductCard.jsx';
import ProductViz from '../components/ProductViz.jsx';
import Breadcrumb from '../components/Breadcrumb.jsx';

export default function Product({ sku, nav }) {
  const { products, addToCart, setDrawerOpen } = useStore();
  const { t, lang } = useI18n();
  const p = products.find((x) => x.sku === sku || x.id === sku);
  const [color, setColor] = useState(null);
  const [opt, setOpt] = useState([]);
  const [qty, setQtyN] = useState(1);
  const [thumb, setThumb] = useState(0);

  useEffect(() => {
    if (!p) return;
    setColor(p.colors && p.colors.length ? p.colors[0] : null);
    setOpt((p.options || []).map(() => 0));
    setQtyN(1);
    setThumb(0);
  }, [p?.sku]);

  if (!p) {
    return (
      <section className="blk"><div className="wrap" style={{ textAlign: 'center', padding: '60px 0' }}>
        <h2 style={{ textTransform: 'uppercase' }}>{t('pdp.notFound')}</h2>
        <p style={{ color: 'var(--ink-2)' }}>{t('pdp.notFoundSub')}</p>
        <button className="btn" onClick={() => nav('/c/all')}>{t('pdp.viewAll')}</button>
      </div></section>
    );
  }

  const sold = p.tag === 'sold';
  const unit = computeUnit(p, opt);

  let thumbs = [];
  if (p.imgs && p.imgs.length) thumbs = p.imgs.map((_, i) => ({ i, tint: null }));
  else if (p.colors && p.colors.length > 1) thumbs = p.colors.map((c, i) => ({ i, tint: colorHex(c) }));
  const mainTint = (thumbs[thumb] && thumbs[thumb].tint) || null;

  const pickThumb = (i) => {
    setThumb(i);
    if (!p.imgs?.length && p.colors && p.colors[i] !== undefined && p.colors.length > 1) setColor(p.colors[i]);
  };
  const pickColor = (c) => {
    setColor(c);
    if (!p.imgs?.length) setThumb((p.colors || []).indexOf(c));
  };

  // สินค้าที่เกี่ยวข้อง: ใช้รายการที่แอดมินผูกไว้ก่อน (related skus) — ไม่ได้ผูก = หมวดเดียวกันอัตโนมัติ
  const pinned = (p.related || [])
    .map((sku) => products.find((x) => x.sku === sku || x.id === sku))
    .filter(Boolean);
  const related = pinned.length
    ? [...pinned, ...products.filter((x) => x.cat === p.cat && x.id !== p.id && !pinned.includes(x))].slice(0, 4)
    : products.filter((x) => x.cat === p.cat && x.id !== p.id).slice(0, 4);
  const like = products.filter((x) => x.cat !== p.cat && x.tag !== 'sold' && !related.includes(x)).slice(0, 4);

  const add = () => {
    if (unit === null) { window.location.href = `mailto:${CONFIG.email}?subject=สอบถามราคา ${p.name} (${p.sku})`; return; }
    const ok = addToCart(p.id, color, opt.slice(), qty);
    if (ok) setDrawerOpen(true);
  };

  return (
    <>
      <Breadcrumb nav={nav} items={[
        { label: t('bc.home'), path: '/' },
        { label: CATS[p.cat]?.t || t('pdp.product'), path: `/c/${p.cat}` },
        { label: p.name },
      ]} />
      <section style={{ padding: '0 0 40px' }}>
        <div className="wrap">
          <div className="pdp">
            <div className="media">
              <div className="main">
                <ProductViz product={p} size="big" imgIndex={p.imgs?.length ? thumb : 0} tint={mainTint} />
                {sold && <span className="tag sold">Sold out</span>}
              </div>
              {thumbs.length > 1 && (
                <div className="thumbs">
                  {thumbs.map((tb, i) => (
                    <div key={i} className={`thumb${thumb === i ? ' on' : ''}`} onClick={() => pickThumb(i)}>
                      <ProductViz product={p} size="sm" imgIndex={tb.i} tint={tb.tint} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="det">
              <div className="cat">{CATS[p.cat]?.t}</div>
              <h1>{p.name}</h1>
              <div className="sku">SKU {p.sku}</div>
              <div className="price">
                {unit !== null ? fmt(unit) : <span style={{ color: 'var(--slate-deep)' }}>{t('pdp.ask')} · {CONFIG.email}</span>}
              </div>
              <p className="blurb">{lang === 'en' && p.blurb_en ? p.blurb_en : p.blurb}</p>

              {p.colors && p.colors.length > 1 && (
                <>
                  <div className="optlab">{t('pdp.color', color)}</div>
                  <div className="opts">
                    {p.colors.map((c) => (
                      <button key={c} className={`opt color${color === c ? ' on' : ''}`} onClick={() => pickColor(c)}>
                        <i style={{ background: colorHex(c) }} />{c}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {(p.options || []).map((o, oi) => (
                <div key={o.lab}>
                  <div className="optlab">{o.lab}</div>
                  <div className="opts">
                    {o.vals.map((v, vi) => (
                      <button key={v} className={`opt${opt[oi] === vi ? ' on' : ''}`}
                        onClick={() => setOpt((prev) => prev.map((x, xi) => (xi === oi ? vi : x)))}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="buyrow">
                {sold ? (
                  <button className="btn full" disabled>{t('pdp.soldOut')}</button>
                ) : (
                  <>
                    <div className="qtybig">
                      <button onClick={() => setQtyN((q) => Math.max(1, q - 1))}>−</button>
                      <span>{qty}</span>
                      <button onClick={() => setQtyN((q) => q + 1)}>＋</button>
                    </div>
                    <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={add}>
                      {unit !== null ? t('pdp.add', fmt(unit * qty)) : t('pdp.ask')}
                    </button>
                  </>
                )}
              </div>

              <div className="trust">
                <div>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6"><path d="M5 12l4 4L19 6" /></svg>
                  Sole Distributor Thailand · CAMP STUDIO
                </div>
                <div>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6"><path d="M3 7h13v10H3zM16 10h3l2 3v4h-5" /><circle cx="7" cy="18" r="1.6" /><circle cx="18" cy="18" r="1.6" /></svg>
                  {t('pdp.shipWorld')}
                </div>
              </div>

              <div className="spec">
                <div className="r"><span>{t('pdp.spec.cat')}</span><b>{CATS[p.cat]?.t}</b></div>
                <div className="r"><span>{t('pdp.spec.weight')}</span><b>{p.weight >= 1000 ? `${p.weight / 1000} kg` : `${p.weight} g`}</b></div>
                <div className="r"><span>SKU</span><b>{p.sku}</b></div>
                <div className="r"><span>{t('pdp.spec.warranty')}</span><b>Limited Warranty</b></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="blk" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="wrap">
          <div className="shead">
            <div><div className="sub">{t('pdp.related.sub')}</div><h2>{t('pdp.related.h')}</h2></div>
            <button className="btn ghost sm" onClick={() => nav(`/c/${p.cat}`)}>{t('pdp.related.view', CATS[p.cat]?.t || '')}</button>
          </div>
          <div className="grid">{related.map((x) => <ProductCard key={x.id} product={x} nav={nav} />)}</div>
        </div>
      </section>

      <section className="blk" style={{ background: 'var(--warm)', borderTop: '1px solid var(--line)' }}>
        <div className="wrap">
          <div className="shead"><div><div className="sub">{t('pdp.foryou.sub')}</div><h2>{t('pdp.foryou.h')}</h2></div></div>
          <div className="grid">{like.map((x) => <ProductCard key={x.id} product={x} nav={nav} />)}</div>
        </div>
      </section>
    </>
  );
}
